/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { ICondition } from '@ahryman40k/ts-fhir-types/lib/R4';
import { forwardRef, useRef, useState } from 'react';
import Button from '@mui/material/Button';

import { useAppSelector } from '../../../app/hooks';
import { selectFhirProblemList } from '../Fhir/fhirSlice';
import FHIRAccordion from '../Fhir/FHIRAccordion';
import { DocumentVersionType, fhirPatientDataTypes } from '../algorithm/types';
import Styles from './CurrentProblemListContainer.module.scss';
import { formatDate } from '../utils';
import { showPatientProblemListAndPastMedicalHx } from '../permissions';
import { getIcd10Codes } from '../Fhir/icd10';
import { getCodeableConceptText } from '../Fhir/codeableConcept';

export function CurrentProblemListContainer({
  documentVersion,
  problemList: problemListOverride,
  defaultCollapsed = true,
}: {
  documentVersion?: DocumentVersionType;
  problemList?: ICondition[];
  defaultCollapsed?: boolean;
}) {
  const storedProblemList = useAppSelector(selectFhirProblemList);
  const problemList = problemListOverride ?? storedProblemList;
  const divRef = useRef<HTMLDivElement>(null);
  const enabled = problemListOverride !== undefined || showPatientProblemListAndPastMedicalHx();

  if (!problemList?.length || !enabled) {
    return null;
  }
  const fhirDataType = fhirPatientDataTypes.find((x) => x.id === 'Problem List');

  return (
    <FHIRAccordion
      heading={fhirDataType?.value}
      divRef={divRef}
      defaultCollapsed={defaultCollapsed}
      mixpanelWidgetName="problem_list"
      documentVersion={documentVersion}
    >
      <ProblemListWidget ref={divRef} problemList={problemList} />
    </FHIRAccordion>
  );
}

interface IProblemListWidget {
  problemList: ICondition[];
}

/* Displays the problemList */
const ProblemListWidget = forwardRef<HTMLDivElement, IProblemListWidget>(({ problemList }, ref) => {
  return (
    <div ref={ref} style={{ padding: '0 0 5px 0' }}>
      {problemList.map((problem: ICondition, index: number) => (
        <ProblemComponent key={index} problem={problem} />
      ))}
    </div>
  );
});
const infoDiv = (key: string, value: string | undefined) => (value ? <div>{`${key} : ${value}`}</div> : null);
const ProblemComponent = ({ problem }: { problem: ICondition }) => {
  const [isOpen, setIsOpen] = useState(false);
  const problemName = getCodeableConceptText(problem.code);
  if (!problemName) {
    return null;
  }
  const recordedDate = formatDate(problem.recordedDate);
  const hasDetails = Boolean(recordedDate || problem.severity?.text);
  const icd10Codes = getIcd10Codes(problem.code);
  return (
    <div className={Styles.problemContainer}>
      <div className={Styles.problemHeader}>
        <div className={Styles.problemContent}>
          <span className={Styles.problemTitle}>{problemName}</span>
          <span className={Styles.icd10Code} aria-hidden={icd10Codes.length === 0}>
            {icd10Codes.length > 0 ? `ICD-10: ${icd10Codes.join(', ')}` : '\u00a0'}
          </span>
        </div>
        {hasDetails && (
          <Button className={Styles.toggleButton} variant="outlined" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? 'close' : 'open'}
          </Button>
        )}
      </div>
      {hasDetails && isOpen && (
        <div className={Styles.problemInfo}>
          {infoDiv('Recorded on', recordedDate)}
          {infoDiv('Severity', problem.severity?.text)}
        </div>
      )}
    </div>
  );
};
